/* Epic — compare from the grid.

   Selection lives in sessionStorage so a filter, a sort or a page of
   pagination does not throw away what you had ticked — all three replace the
   grid markup, and all three are things you do WHILE comparing. The tray
   rebuilds itself from storage whenever the DOM changes.

   The table is built from the cards' own quick-view payloads, so there is no
   second source for the numbers. Row keys are unioned across the selected
   products rather than intersected: if one fabric publishes tear strength and
   another does not, the row stays and the gap shows. Intersecting would
   quietly delete the most interesting comparison on the page, which is the
   one where somebody has the figure and somebody has not. */
(() => {
  const MAX = 4;
  const KEY = 'epic-compare-picks';

  const tray = document.querySelector('[data-epic-cmt]');
  const dialog = document.querySelector('[data-cmt-dialog]');
  if (!tray || !dialog) return;

  const elN = tray.querySelector('[data-cmt-n]');
  const elChips = tray.querySelector('[data-cmt-chips]');
  const btnOpen = tray.querySelector('[data-cmt-open]');
  const btnClear = tray.querySelector('[data-cmt-clear]');
  const table = dialog.querySelector('[data-cmt-table]');
  const note = dialog.querySelector('[data-cmt-note]');

  /** @type {Map<string, object>} handle -> payload */
  let picks = new Map();

  function load() {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) picks = new Map(JSON.parse(raw));
    } catch (e) {
      picks = new Map(); // private mode, or someone else's data in the slot
    }
  }
  function save() {
    try {
      sessionStorage.setItem(KEY, JSON.stringify([...picks]));
    } catch (e) {
      /* a tray that forgets is better than one that throws */
    }
  }

  function payloadFor(btn) {
    const holder = btn.closest('[data-epic-qv-holder]') || btn.parentElement;
    const json = holder && holder.querySelector('script[data-epic-qv]');
    if (!json) return null;
    try {
      return JSON.parse(json.textContent);
    } catch (e) {
      return null;
    }
  }

  function keyOf(d) {
    return d.url || d.title;
  }

  function render() {
    const n = picks.size;
    elN.textContent = String(n);
    tray.hidden = n === 0;
    btnOpen.disabled = n < 2;
    btnOpen.textContent = n < 2 ? 'Pick one more' : `Compare ${n}`;

    elChips.replaceChildren();
    for (const [k, d] of picks) {
      const li = document.createElement('li');
      li.style.setProperty('--chip-ac', d.accent || 'currentColor');
      const name = document.createElement('span');
      name.textContent = d.title;
      const drop = document.createElement('button');
      drop.type = 'button';
      drop.setAttribute('aria-label', `Remove ${d.title}`);
      drop.textContent = '×';
      drop.addEventListener('click', () => {
        picks.delete(k);
        save();
        render();
        syncTicks();
      });
      li.append(name, drop);
      elChips.append(li);
    }
  }

  /* The grid re-renders on filter and sort, so the ticks are restored from
     storage rather than being the source of truth. */
  function syncTicks() {
    document.querySelectorAll('[data-epic-cmt-tick]').forEach((input) => {
      const d = payloadFor(input);
      input.checked = !!(d && picks.has(keyOf(d)));
      const full = picks.size >= MAX && !input.checked;
      input.disabled = full;
      const label = input.closest('label');
      if (label) label.classList.toggle('is-full', full);
    });
  }

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-epic-cmt-tick]');
    if (!input) return;
    const d = payloadFor(input);
    if (!d) return;
    const k = keyOf(d);
    if (input.checked) {
      if (picks.size >= MAX) {
        input.checked = false;
        return;
      }
      picks.set(k, d);
    } else {
      picks.delete(k);
    }
    save();
    render();
    syncTicks();
  });

  btnClear.addEventListener('click', () => {
    picks.clear();
    save();
    render();
    syncTicks();
  });

  function buildTable() {
    const items = [...picks.values()];

    // Union of row labels, in the order they are first seen, so the ordering
    // the Liquid chose (headline numbers first, measured rows after) survives.
    const order = [];
    const seen = new Set();
    items.forEach((d) =>
      (d.rows || []).forEach((r) => {
        if (!seen.has(r.k)) {
          seen.add(r.k);
          order.push(r.k);
        }
      })
    );

    table.replaceChildren();

    const head = document.createElement('thead');
    const hr = document.createElement('tr');
    hr.append(document.createElement('th'));
    items.forEach((d) => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.style.setProperty('--col-ac', d.accent || 'currentColor');
      const a = document.createElement('a');
      a.href = d.url || '#';
      a.textContent = d.title;
      th.append(a);
      if (d.type) {
        const em = document.createElement('em');
        em.textContent = d.type;
        th.append(em);
      }
      hr.append(th);
    });
    head.append(hr);
    table.append(head);

    const body = document.createElement('tbody');
    order.forEach((label) => {
      const tr = document.createElement('tr');
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = label;
      tr.append(th);

      /* The method belongs to the cell, not to the row, whenever the cells
         disagree. An earlier version hoisted whichever method it saw last
         onto the row label, which put Rio's "from 41% shading" next to
         Halo's directly measured figure and silently claimed a provenance
         that was not Halo's. On a site whose whole argument is that the
         numbers are real, that is the worst available bug.

         Where every method present agrees, it goes on the label once and the
         cells stay clean. Where they differ, each cell carries its own. */
      const cells = items.map((d) => ({
        d,
        row: (d.rows || []).find((r) => r.k === label),
      }));
      const methods = [...new Set(cells.map((c) => c.row && c.row.m).filter(Boolean))];
      const shared = methods.length === 1 ? methods[0] : '';

      cells.forEach(({ row }) => {
        const td = document.createElement('td');
        if (row) {
          td.textContent = row.v;
          if (row.m && !shared) {
            const m = document.createElement('span');
            m.className = 'epic-cmt__method';
            m.textContent = row.m;
            td.append(m);
          }
        } else {
          // Not "0", not blank: an unmeasured figure is a different thing
          // from a low one, and the whole position rests on not blurring it.
          td.textContent = 'not published';
          td.className = 'epic-cmt__gap';
        }
        tr.append(td);
      });

      if (shared) {
        const m = document.createElement('span');
        m.className = 'epic-cmt__method';
        m.textContent = shared;
        th.append(m);
      }
      body.append(tr);
    });

    // Ink compatibility, which is a set rather than a figure.
    const inkRow = document.createElement('tr');
    const inkTh = document.createElement('th');
    inkTh.scope = 'row';
    inkTh.textContent = 'Runs on';
    inkRow.append(inkTh);
    items.forEach((d) => {
      const td = document.createElement('td');
      td.textContent = (d.inks || []).join(', ') || 'not published';
      if (!(d.inks || []).length) td.className = 'epic-cmt__gap';
      inkRow.append(td);
    });
    body.append(inkRow);

    const priceRow = document.createElement('tr');
    const priceTh = document.createElement('th');
    priceTh.scope = 'row';
    priceTh.textContent = 'List';
    priceRow.append(priceTh);
    items.forEach((d) => {
      const td = document.createElement('td');
      td.textContent = d.price || 'priced on request';
      if (!d.price) td.className = 'epic-cmt__gap';
      priceRow.append(td);
    });
    body.append(priceRow);

    table.append(body);

    const gaps = table.querySelectorAll('.epic-cmt__gap').length;
    note.textContent = gaps
      ? `${gaps} figure${gaps === 1 ? '' : 's'} not published. A blank is not a zero — it means nobody has measured it, and Epic would rather say so than fill it in.`
      : 'Every figure here is measured and published.';
  }

  btnOpen.addEventListener('click', () => {
    if (picks.size < 2) return;
    buildTable();
    dialog.showModal();
  });

  dialog.addEventListener('click', (e) => {
    if (e.target.closest('[data-cmt-close]')) dialog.close();
    if (e.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    const inside =
      e.clientX >= r.left && e.clientX <= r.right &&
      e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) dialog.close();
  });

  load();
  render();
  syncTicks();

  /* Horizon swaps the grid's markup on filter, sort and pagination. Restoring
     the ticks on mutation is cheaper than finding every event that does it,
     and it is correct for the ones nobody has thought of yet. */
  const grid = document.querySelector('.product-grid');
  if (grid && 'MutationObserver' in window) {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        syncTicks();
      });
    }).observe(grid, { childList: true, subtree: true });
  }
})();
